import { useEffect, useMemo, useState } from "react";
import { Check, Forward, Info, Loader2, Search, User, Users, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { resolveApiUrl } from "@/lib/api/client";
import { fetchInternalChatPartners } from "@/lib/api/users";
import { createInternalThread, ensurePatientThread, fetchThreads } from "@/lib/api/threads";
import { PatientSearchDropdown } from "@/components/messages/patient-search-dropdown";
import { buildForwardedStamp, forwardMessagesToThread } from "@/lib/messages/forward-message";
import { getRecentForwardTargets, recordForwardTarget } from "@/lib/messages/forward-recents";
import { getMessageText, getThreadDisplayName } from "@/lib/messages/messages-helpers";
import { parseAttachments } from "@/lib/realtime/message-attachments";
import { useHasPermission } from "@/hooks/use-permissions";
import { getAvailabilityMap, getOnlineUserIds, subscribePresence } from "@/lib/realtime/presence-service";
import PresenceDot from "@/components/presence/presence-dot";

function partnerLabel(partner) {
  const composed = [partner?.firstName, partner?.lastName]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ");
  if (composed) return composed;
  const display = String(partner?.displayName || "").trim();
  if (display) return display;
  return String(partner?.id || "").trim() || "Unknown";
}

function patientName(patient) {
  return (
    patient?.displayName
    || `${patient?.profile?.FirstName || ""} ${patient?.profile?.LastName || ""}`.trim()
    || "Unknown Patient"
  );
}

function patientPhone(patient) {
  if (patient?._selectedPhone) return String(patient._selectedPhone).trim();
  const phones = patient?.contacts?.Phones;
  if (Array.isArray(phones) && phones.length > 0) {
    const verified = phones.find((p) => p.Verified);
    return (verified || phones[0])?.E164 || "";
  }
  return "";
}

function threadRowId(thread) {
  return String(thread?.threadId || thread?.id || "").trim();
}

function initialsFor(label) {
  return String(label || "")
    .split(" ")
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");
}

/**
 * Forward one or more messages to another staff member (internal chat, direct or existing group)
 * or to a patient (WhatsApp thread). The copies go out over the normal WS send path with a
 * `metadata.forwarded` provenance stamp (see lib/messages/forward-message.js): internal chats
 * render a "Forwarded" marker from it — a patient-sourced marker links back to the original
 * conversation point — while a patient receiving a forward sees ONLY the plain message.
 *
 * A batch is always ONE destination and N separate messages, in conversation order — never a
 * merged body, so every copy keeps its own attachments and its own backlink.
 *
 * Destinations are permission-gated per module: staff targets need chats:manage, patient
 * targets need messages:manage (mirrors what the person could send by hand in each pane).
 *
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {{ messages: object[], threadId: string, scope: "patient"|"internal", threadTitle: string } | null} props.source
 *   The messages being forwarded (conversation order) plus a snapshot of where they live (taken
 *   when the dialog opened).
 * @param {() => void} [props.onSent] Called once the copies actually went out — lets the caller
 *   drop its selection. Not called when the dialog is merely cancelled.
 * @param {{ botId: string, label: string }[]} [props.whatsappNumbers] Configured business numbers
 *   (send-from choice when forwarding to a patient on a multi-number setup).
 */
export function ForwardMessageDialog({ open, onOpenChange, source, onSent, whatsappNumbers = [] }) {
  const canForwardToStaff = useHasPermission("chats", "manage");
  const canForwardToPatient = useHasPermission("messages", "manage");

  const [tab, setTab] = useState("staff");
  const [partners, setPartners] = useState([]);
  const [groupThreads, setGroupThreads] = useState([]);
  // "Recently forwarded to" snapshots (kind+id+label), re-read every time the dialog opens.
  const [recents, setRecents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [query, setQuery] = useState("");
  // The chosen destination: {kind:"partner"} | {kind:"group"} | {kind:"patient"}.
  const [target, setTarget] = useState(null);
  const [selectedBotId, setSelectedBotId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  // {done,total} while a batch is being prepared (attachments re-download one message at a time).
  const [progress, setProgress] = useState(null);

  // Presence dots for the staff rows: one open-gated subscription (same recipe as NewChatDialog).
  const [presenceTick, setPresenceTick] = useState(0);
  useEffect(() => {
    if (!open) return undefined;
    setPresenceTick((n) => n + 1);
    return subscribePresence(() => setPresenceTick((n) => n + 1));
  }, [open]);
  const onlineIds = getOnlineUserIds();
  const availabilityMap = getAvailabilityMap();
  void presenceTick;

  // Reset + load the staff directory (people and existing group chats) whenever the dialog opens.
  useEffect(() => {
    if (!open) return undefined;
    setTab(canForwardToStaff ? "staff" : "patient");
    setQuery("");
    setTarget(null);
    setSubmitting(false);
    setSubmitError("");
    setProgress(null);
    setLoadError("");
    setRecents(getRecentForwardTargets());
    if (!canForwardToStaff) return undefined;
    setLoading(true);
    let cancelled = false;
    Promise.all([
      fetchInternalChatPartners({ context: "chat" }),
      fetchThreads({ scope: "internal", limit: 100 }),
    ])
      .then(([partnersRes, threadsRes]) => {
        if (cancelled) return;
        setPartners(Array.isArray(partnersRes?.items) ? partnersRes.items : []);
        const rows = Array.isArray(threadsRes?.threads) ? threadsRes.threads : [];
        setGroupThreads(rows.filter((t) => t?.metadata?.internalKind === "group"));
      })
      .catch(() => {
        if (cancelled) return;
        setPartners([]);
        setGroupThreads([]);
        setLoadError("Failed to load people you can chat with.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, canForwardToStaff]);

  // Default the send-from number to the first configured one, keeping it valid as the list loads.
  useEffect(() => {
    if (!Array.isArray(whatsappNumbers) || whatsappNumbers.length === 0) return;
    setSelectedBotId((prev) =>
      prev && whatsappNumbers.some((n) => n.botId === prev) ? prev : whatsappNumbers[0].botId,
    );
  }, [whatsappNumbers]);

  const messages = useMemo(
    () => (Array.isArray(source?.messages) ? source.messages.filter(Boolean) : []),
    [source],
  );
  const count = messages.length;
  // The preview quotes the first message and totals the rest — a full list would push the picker
  // (the part that actually needs the room) off a short window.
  const previewText = useMemo(() => getMessageText(messages[0]), [messages]);
  const previewAttachmentCount = useMemo(
    () => messages.reduce((total, msg) => total + parseAttachments(msg).length, 0),
    [messages],
  );

  const q = query.trim().toLowerCase();
  const filteredPartners = useMemo(() => {
    if (!q) return partners;
    return partners.filter((partner) => {
      const label = partnerLabel(partner).toLowerCase();
      const type = String(partner?.userType || "").toLowerCase();
      return label.includes(q) || type.includes(q);
    });
  }, [partners, q]);
  const filteredGroups = useMemo(() => {
    if (!q) return groupThreads;
    return groupThreads.filter((thread) =>
      getThreadDisplayName(thread, { isInternal: true }).label.toLowerCase().includes(q),
    );
  }, [groupThreads, q]);

  const selectedPatient = target?.kind === "patient" ? target.patient : null;
  const selectedPatientPhone = selectedPatient ? patientPhone(selectedPatient) : "";
  const targetLabel =
    target?.kind === "partner"
      ? partnerLabel(target.partner)
      : target?.kind === "group"
        ? getThreadDisplayName(target.thread, { isInternal: true }).label
        : target?.kind === "patient"
          ? patientName(target.patient)
          : "";

  const canSubmit =
    !submitting &&
    count > 0 &&
    (target?.kind === "partner" ||
      target?.kind === "group" ||
      (target?.kind === "patient" && Boolean(selectedPatient?.id) && Boolean(selectedPatientPhone)));

  const switchTab = (next) => {
    if (next === tab) return;
    setTab(next);
    setTarget(null);
    setSubmitError("");
  };

  const handleForward = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError("");
    setProgress({ done: 0, total: count });
    try {
      let threadId = "";
      if (target.kind === "partner") {
        const res = await createInternalThread({ userIds: [String(target.partner.id)], kind: "direct" });
        threadId = String(res?.threadId || "").trim();
      } else if (target.kind === "group") {
        threadId = threadRowId(target.thread);
      } else {
        const res = await ensurePatientThread({
          patientUserId: String(target.patient.id),
          phone: selectedPatientPhone,
          whatsappBotId: selectedBotId || undefined,
        });
        threadId = String(res?.threadId || "").trim();
      }
      if (!threadId) {
        throw new Error("Could not resolve the destination conversation.");
      }
      // Stamped per message, never once for the batch: a selection can mix fresh messages with
      // already-forwarded ones, and buildForwardedStamp keeps the latter pointing at their true
      // origin. In the merged WhatsApp view it also lets a selection that spans sibling clinic
      // threads stamp each copy with the thread it actually came from.
      const items = messages.map((msg) => ({
        message: msg,
        forwarded: buildForwardedStamp({
          message: msg,
          sourceThreadId: source?.threadId,
          sourceScope: source?.scope === "patient" ? "patient" : "internal",
          sourceTitle: source?.threadTitle,
        }),
      }));
      const { sent, skipped } = await forwardMessagesToThread({
        threadId,
        items,
        onProgress: (done, total) => setProgress({ done, total }),
      });
      recordForwardTarget(
        target.kind === "partner"
          ? { kind: "partner", id: String(target.partner.id), label: targetLabel }
          : target.kind === "group"
            ? { kind: "group", id: threadRowId(target.thread), label: targetLabel }
            : { kind: "patient", id: String(target.patient.id), label: targetLabel, phone: selectedPatientPhone },
      );
      const what = sent === 1 ? "Message" : `${sent} messages`;
      toast.success(
        skipped > 0
          ? `${what} forwarded to ${targetLabel} · ${skipped} skipped (nothing to send)`
          : `${what} forwarded to ${targetLabel}`,
      );
      onSent?.();
      onOpenChange?.(false);
    } catch (error) {
      setSubmitError(error?.message || "Could not forward the message. Please try again.");
    } finally {
      setSubmitting(false);
      setProgress(null);
    }
  };

  // "+ 4 more · 2 attachments" — whichever halves apply.
  const summaryLine = [
    count > 1 ? `+ ${count - 1} more` : "",
    previewAttachmentCount === 1
      ? "1 attachment"
      : previewAttachmentCount > 1
        ? `${previewAttachmentCount} attachments`
        : "",
  ]
    .filter(Boolean)
    .join(" · ");

  const sectionHeaderClassName =
    "px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground";

  const renderPartnerRow = (partner, key) => {
    const id = String(partner?.id || "").trim();
    const label = partnerLabel(partner);
    const userType = String(partner?.userType || "").trim();
    const selected = target?.kind === "partner" && String(target.partner?.id) === id;
    const online = onlineIds.has(id);
    return (
      <button
        key={key}
        type="button"
        onClick={() => {
          setSubmitError("");
          setTarget(selected ? null : { kind: "partner", partner });
        }}
        className={cn(
          "flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted",
          selected && "bg-muted",
        )}
      >
        <span className="relative shrink-0">
          <Avatar className="h-8 w-8">
            <AvatarImage src={resolveApiUrl(partner?.avatarUrl)} />
            <AvatarFallback className="bg-primary/10 text-[10px] font-semibold text-primary">
              {initialsFor(label)}
            </AvatarFallback>
          </Avatar>
          {/* Offline renders nothing — PresenceDot's own contract. */}
          <PresenceDot
            online={online}
            availability={availabilityMap[id] || partner?.availability}
            className="absolute -bottom-0.5 -right-0.5"
          />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-medium text-foreground">{label}</span>
          {userType ? (
            <span className="truncate text-[11px] capitalize text-muted-foreground">{userType}</span>
          ) : null}
        </span>
        <Check className={cn("h-4 w-4 shrink-0 text-primary", selected ? "opacity-100" : "opacity-0")} />
      </button>
    );
  };

  const renderGroupRow = (thread, key) => {
    const id = threadRowId(thread);
    const label = getThreadDisplayName(thread, { isInternal: true }).label;
    const memberCount = Array.isArray(thread?.userIds) ? thread.userIds.length : 0;
    const selected = target?.kind === "group" && threadRowId(target.thread) === id;
    return (
      <button
        key={key}
        type="button"
        onClick={() => {
          setSubmitError("");
          setTarget(selected ? null : { kind: "group", thread });
        }}
        className={cn(
          "flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted",
          selected && "bg-muted",
        )}
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <Users className="h-4 w-4" aria-hidden />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-medium text-foreground">{label}</span>
          <span className="truncate text-[11px] text-muted-foreground">
            {memberCount > 0 ? `Group · ${memberCount} members` : "Group"}
          </span>
        </span>
        <Check className={cn("h-4 w-4 shrink-0 text-primary", selected ? "opacity-100" : "opacity-0")} />
      </button>
    );
  };

  // The three most recent destinations, pinned above the full lists while not searching.
  // Snapshots resolve against the LIVE directory — an entry whose partner left the chat
  // directory (or whose group was deleted) is skipped rather than shown stale.
  const recentStaffRows = q
    ? []
    : recents
        .map((entry) => {
          if (entry.kind === "partner") {
            const partner = partners.find((p) => String(p?.id || "").trim() === entry.id);
            return partner ? renderPartnerRow(partner, `recent-p-${entry.id}`) : null;
          }
          if (entry.kind === "group") {
            const thread = groupThreads.find((t) => threadRowId(t) === entry.id);
            return thread ? renderGroupRow(thread, `recent-g-${entry.id}`) : null;
          }
          return null;
        })
        .filter(Boolean)
        .slice(0, 3);
  // Patient recents carry their own snapshot (label + phone) — no directory round trip needed.
  const recentPatients = recents.filter((entry) => entry.kind === "patient" && entry.phone).slice(0, 3);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Height is viewport-capped and the LIST is the only part that shrinks/scrolls — without
          this the dialog grows past short (Electron) windows and clips off-screen top+bottom,
          since the Radix panel is centered at 50% with no max-height of its own. */}
      <DialogContent className="flex max-h-[min(85svh,42rem)] flex-col overflow-hidden sm:max-w-md">
        <DialogHeader className="shrink-0">
          <DialogTitle>{count > 1 ? `Forward ${count} messages` : "Forward message"}</DialogTitle>
          <DialogDescription>
            {count > 1
              ? "They go to one conversation, as separate messages, in the order they were sent."
              : "Send a copy of this message to another conversation."}
          </DialogDescription>
        </DialogHeader>

        {/* What is being forwarded — snapshot preview, quoted-block styling. */}
        <div className="shrink-0 rounded-md border-l-4 border-primary/70 bubble-surface-muted px-2.5 py-1.5">
          <p className="flex items-center gap-1 text-xs font-semibold text-primary">
            <Forward className="h-3 w-3 shrink-0" aria-hidden />
            {count > 1 ? `Forwarding ${count} messages` : "Forwarding"}
          </p>
          {previewText ? (
            <p className="line-clamp-2 wrap-break-word text-xs text-muted-foreground">{previewText}</p>
          ) : null}
          {summaryLine ? <p className="text-xs text-muted-foreground">{summaryLine}</p> : null}
        </div>

        {/* Destination kind — same joined segmented control as the "New message" dialog. */}
        {canForwardToStaff && canForwardToPatient ? (
          <div className="inline-flex w-full shrink-0 rounded-lg border border-border bg-muted/40 p-0.5">
            {[
              { key: "staff", label: "Staff", Icon: Users },
              { key: "patient", label: "Patient", Icon: User },
            ].map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => switchTab(key)}
                disabled={submitting}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                  tab === key
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                {label}
              </button>
            ))}
          </div>
        ) : null}

        {tab === "staff" ? (
          <div className="flex min-h-0 flex-col rounded-md border">
            <div className="flex shrink-0 items-center border-b px-3">
              <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search staff and groups…"
                className="h-10 border-0 px-0 shadow-none focus-visible:ring-0"
              />
            </div>
            {/* Fixed height while there's room; min-h-0 lets a short window compress it instead
                of pushing the footer (or the whole panel) off-screen. */}
            <ScrollArea className="h-64 min-h-0">
              <div className="p-1">
                {loading ? (
                  <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading…
                  </div>
                ) : loadError ? (
                  <div className="py-6 text-center text-sm text-destructive">{loadError}</div>
                ) : filteredPartners.length === 0 && filteredGroups.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">No staff found.</div>
                ) : (
                  <>
                    {recentStaffRows.length > 0 ? (
                      <>
                        <p className={sectionHeaderClassName}>Recent</p>
                        {recentStaffRows}
                        {filteredPartners.length > 0 ? (
                          <p className={sectionHeaderClassName}>People</p>
                        ) : null}
                      </>
                    ) : null}
                    {filteredPartners.map((partner) =>
                      renderPartnerRow(partner, `p-${String(partner?.id || "").trim()}`),
                    )}
                    {filteredGroups.length > 0 ? (
                      <p className={sectionHeaderClassName}>Groups</p>
                    ) : null}
                    {filteredGroups.map((thread) => renderGroupRow(thread, `g-${threadRowId(thread)}`))}
                  </>
                )}
              </div>
            </ScrollArea>
          </div>
        ) : (
          // Naturally short, but scrollable when a small window squeezes the dialog — the
          // selected chip + send-from + hint stack must never push the footer out of reach.
          <ScrollArea className="min-h-0">
            <div className="flex flex-col gap-3">
            {selectedPatient ? (
              <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2.5">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-[11px] font-bold text-amber-700 ring-1 ring-amber-400/30 dark:bg-amber-500/20 dark:text-amber-300">
                  {patientName(selectedPatient).split(" ").slice(0, 2).map((p) => p[0]?.toUpperCase() || "").join("")}
                </div>
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-medium text-foreground">{patientName(selectedPatient)}</span>
                  {selectedPatientPhone ? (
                    <span className="font-mono text-[11px] text-muted-foreground">{selectedPatientPhone}</span>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => setTarget(null)}
                  className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted"
                  title="Choose a different patient"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <>
                <PatientSearchDropdown
                  onSelectPatient={(patient) => {
                    setSubmitError("");
                    setTarget({ kind: "patient", patient });
                  }}
                  autoFocus
                  label="Find patient"
                />
                {recentPatients.length > 0 ? (
                  <div className="flex flex-col">
                    <p className="pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Recent
                    </p>
                    {recentPatients.map((entry) => (
                      <button
                        key={`recent-pt-${entry.id}`}
                        type="button"
                        onClick={() => {
                          setSubmitError("");
                          setTarget({
                            kind: "patient",
                            patient: { id: entry.id, displayName: entry.label, _selectedPhone: entry.phone },
                          });
                        }}
                        className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted"
                      >
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-[11px] font-bold text-amber-700 ring-1 ring-amber-400/30 dark:bg-amber-500/20 dark:text-amber-300">
                          {initialsFor(entry.label)}
                        </span>
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate text-sm font-medium text-foreground">{entry.label}</span>
                          <span className="font-mono text-[11px] text-muted-foreground">{entry.phone}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </>
            )}

            {selectedPatient && !selectedPatientPhone ? (
              <p className="text-sm text-destructive">This patient has no phone number to message.</p>
            ) : null}

            {whatsappNumbers.length > 1 ? (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="forward-wa-send-from" className="text-xs font-medium text-muted-foreground">
                  Send from
                </label>
                <select
                  id="forward-wa-send-from"
                  value={selectedBotId}
                  onChange={(e) => setSelectedBotId(e.target.value)}
                  disabled={submitting}
                  className="h-9 w-full rounded-md border border-border bg-background px-2.5 text-sm text-foreground shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {whatsappNumbers.map((n) => (
                    <option key={n.botId} value={n.botId}>
                      {n.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            <div className="flex items-start gap-2 rounded-md bg-amber-500/10 px-3 py-2 text-[11px] text-amber-700 dark:text-amber-300">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                The patient receives the plain message on WhatsApp — no &ldquo;forwarded&rdquo; label. The
                first message to a new contact may require an approved template.
              </span>
            </div>
            </div>
          </ScrollArea>
        )}

        {submitError ? <p className="shrink-0 text-sm text-destructive">{submitError}</p> : null}

        <DialogFooter className="shrink-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button type="button" onClick={handleForward} disabled={!canSubmit}>
            {submitting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Forward className="mr-1 h-4 w-4" />}
            {submitting
              ? progress && progress.total > 1 && progress.done < progress.total
                ? `Preparing ${progress.done + 1} of ${progress.total}…`
                : "Forwarding…"
              : count > 1
                ? `Forward ${count}`
                : "Forward"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ForwardMessageDialog;
